import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, TrendingDown, DollarSign, Activity } from 'lucide-react';

const GlobalStats = ({ metrics }) => {
  if (!metrics || !metrics.quote) return null;

  const usdData = metrics.quote.USD;
  const marketCapChange = usdData.total_market_cap_yesterday_percentage_change || 0;
  const volumeChange = usdData.total_volume_24h_yesterday_percentage_change || 0;

  const formatNumber = (num) => {
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toFixed(2)}`;
  };

  const formatPercent = (num) => {
    const formatted = Math.abs(num).toFixed(2);
    return num >= 0 ? `+${formatted}%` : `-${formatted}%`;
  };

  const stats = [
    {
      title: 'Total Market Cap',
      value: formatNumber(usdData.total_market_cap),
      change: marketCapChange,
      icon: DollarSign,
      color: 'indigo'
    },
    {
      title: '24h Volume',
      value: formatNumber(usdData.total_volume_24h),
      change: volumeChange,
      icon: Activity,
      color: 'purple'
    },
    {
      title: 'BTC Dominance',
      value: `${metrics.btc_dominance?.toFixed(2)}%`,
      change: null,
      icon: TrendingUp,
      color: 'blue'
    },
    {
      title: 'Active Cryptos',
      value: metrics.active_cryptocurrencies?.toLocaleString() || '0',
      change: null,
      icon: Activity,
      color: 'violet'
    }
  ];

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
      data-testid="global-stats"
    >
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        const isPositive = stat.change !== null && stat.change >= 0;
        
        return (
          <motion.div key={index} variants={item} className="h-full">
            <Card 
              className="border hover:shadow-lg transition-shadow h-full flex flex-col" 
              style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}
              data-testid={`stat-card-${index}`}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2 flex-shrink-0">
                <CardTitle className="text-sm font-medium" style={{ color: '#475569' }}>
                  {stat.title}
                </CardTitle>
                <div className="p-2 rounded-lg" style={{ backgroundColor: '#EFF6FF' }}>
                  <Icon className="w-4 h-4" style={{ color: '#1D4ED8' }} />
                </div>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-center">
                <motion.div 
                  key={stat.value}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-2xl font-bold"
                  style={{ color: '#0F172A' }}
                  data-testid={`stat-value-${index}`}
                >
                  {stat.value}
                </motion.div>
                {stat.change !== null ? (
                  <div className="flex items-center gap-1 mt-1 text-sm font-medium" style={{ color: isPositive ? '#16A34A' : '#DC2626' }}>
                    {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    <span data-testid={`stat-change-${index}`}>{formatPercent(stat.change)}</span>
                  </div>
                ) : (
                  <div className="h-6 mt-1"></div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

export default GlobalStats;
